import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, AlertCircleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function PricingPreview() {
  const webDesignFeatures = ['Up to 5 pages', 'Responsive mobile design', 'Enquiry form', 'Basic SEO setup'];
  const ecommerceFeatures = ['Unlimited products', 'PayPal/Stripe integration', 'Responsive shop layout', '3-months free hosting'];
  const maintenanceFeatures = ['Hosting & domain management', 'Security monitoring', 'Monthly backups', 'Plugin/theme updates'];
  return <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-381">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-382">
        <div className="max-w-6xl mx-auto" data-id="element-383">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-384">
            <div className="inline-flex items-center gap-2 bg-[#0DC3DC]/10 px-4 py-2 rounded-full mb-4" data-id="element-385">
              <AlertCircleIcon size={20} className="text-[#0DC3DC]" data-id="element-386" />
              <span className="text-sm font-medium text-[#0DC3DC]" data-id="element-387">
                Starting prices shown below
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-388">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-389">
              Every website is built specifically around your business needs.
              Final pricing is tailored during your free consultation.
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-390">
            {/* Web Design */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-[#212C34] rounded-lg p-6 border border-[#D4AF37] shadow-lg flex flex-col" data-id="element-391">
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-392">Web Design</h3>
              <div className="mb-4" data-id="element-393">
                <span className="text-sm text-white/70" data-id="element-394">Starting from</span>
                <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-395">£399</p>
              </div>

              <ul className="space-y-2 mb-6 flex-grow" data-id="element-396">
                {webDesignFeatures.map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-397">
                    <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-398" />
                    <span data-id="element-399">{feature}</span>
                  </li>)}
              </ul>

              <Link to="/pricing" className="w-full bg-[#0DC3DC] hover:bg-[#26D0E8] text-white py-3 rounded-lg font-bold transition-all duration-300 text-center block" data-id="element-400">
                View Details
              </Link>
            </motion.div>

            {/* E-Commerce */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-[#212C34] rounded-lg p-6 border-2 border-[#0DC3DC] shadow-xl flex flex-col relative" data-id="element-401">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2" data-id="element-402">
                <span className="bg-[#0DC3DC] text-white px-4 py-1 rounded-full text-xs font-bold" data-id="element-403">
                  POPULAR
                </span>
              </div>

              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-404">E-Commerce</h3>
              <div className="mb-4" data-id="element-405">
                <span className="text-sm text-white/70" data-id="element-406">Starting from</span>
                <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-407">£899</p>
              </div>

              <ul className="space-y-2 mb-6 flex-grow" data-id="element-408">
                {ecommerceFeatures.map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-409">
                    <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-410" />
                    <span data-id="element-411">{feature}</span>
                  </li>)}
              </ul>

              <Link to="/pricing" className="w-full bg-[#0DC3DC] hover:bg-[#26D0E8] text-white py-3 rounded-lg font-bold transition-all duration-300 text-center block" data-id="element-412">
                View Details
              </Link>
            </motion.div>

            {/* Maintenance */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-[#212C34] rounded-lg p-6 border border-[#D4AF37] shadow-lg flex flex-col" data-id="element-413">
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-414">
                Maintenance
              </h3>
              <div className="mb-4" data-id="element-415">
                <span className="text-sm text-white/70" data-id="element-416">Starting from</span>
                <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-417">
                  £79<span className="text-lg" data-id="element-418">/mo</span>
                </p>
              </div>

              <ul className="space-y-2 mb-6 flex-grow" data-id="element-419">
                {maintenanceFeatures.map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-420">
                    <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-421" />
                    <span data-id="element-422">{feature}</span>
                  </li>)}
              </ul>

              <Link to="/pricing" className="w-full bg-[#0DC3DC] hover:bg-[#26D0E8] text-white py-3 rounded-lg font-bold transition-all duration-300 text-center block" data-id="element-423">
                View Details
              </Link>
            </motion.div>
          </div>

          {/* Highlight Box */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-[#0DC3DC]/10 border-l-4 border-[#0DC3DC] p-6 rounded-lg mb-8" data-id="element-424">
            <p className="text-gray-900 dark:text-white font-medium text-center" data-id="element-425">
              <strong data-id="element-426">
                Every website is built specifically around your business needs.
              </strong>{' '}
              A tailored package is created after your free consultation.
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center" data-id="element-427">
            <Link to="/pricing" className="inline-block bg-[#0DC3DC] hover:bg-[#26D0E8] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-428">
              View Full Pricing & Book Free Consultation
            </Link>
          </motion.div>
        </div>
      </div>
    </section>;
}